'use client';

// A client component: the delay is a timer (useState/useEffect), and the `~ui` barrel is imported
// by insights' React Server Components, which cannot import hooks without this directive.
import { type ReactNode, useEffect, useState } from 'react';
import { Center, Loader, Stack, Text } from '@mantine/core';

/** How long a load may take before it is shown at all: below this, a loader is only a flash. */
export const LOADING_DELAY_MS = 300;

/**
 * @desc `false` until `delayMs` has elapsed since mount, then `true`; `true` from the first render
 * when `delayMs` is zero or less. The timer is cleared on unmount, so a load that finishes inside
 * the delay never renders its loading state at all — no DOM, no flash, nothing announced.
 *
 * Client-only: a server render (an insights fragment) has no timer to fire, so it stays `false`.
 */
export function useDelayedFlag(delayMs: number): boolean {
  const [elapsed, setElapsed] = useState(false);
  useEffect(() => {
    if (delayMs <= 0) return;
    const timer = window.setTimeout(() => setElapsed(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs]);
  return delayMs <= 0 || elapsed;
}

export type DelayedProps = {
  children: ReactNode;
  /** Milliseconds to render nothing for. Default `LOADING_DELAY_MS`. */
  delayMs?: number;
};

/**
 * @desc Renders `children` only once `delayMs` has passed — for a section's skeleton inside a page
 * that is already on screen, so a fast load swaps straight from nothing to content.
 */
export function Delayed({ children, delayMs = LOADING_DELAY_MS }: DelayedProps) {
  return useDelayedFlag(delayMs) ? children : null;
}

export type PageLoadingProps = {
  /** What is loading, shown under the loader. Default "Loading…". */
  label?: string;
  /** Milliseconds to render nothing for. Default `LOADING_DELAY_MS`; 0 shows it at once. */
  delayMs?: number;
  /**
   * The block's minimum height. It also grows into a flex column's free space (`flex: 1`), so as
   * the direct child of the portal's content column it centres in the whole content area.
   * Default `50vh`.
   */
  minHeight?: string | number;
};

/**
 * @desc A page's loading state: nothing for the first `delayMs`, then the accent loader and a
 * dimmed label, centred in the content area. No panel of its own, so it sits on whichever scheme
 * surface is beneath it. `role="status"` with polite live updates; the loader itself is hidden
 * from assistive tech because the label says the same thing. Mantine's loader stops animating
 * under reduced motion through the theme's `respectReducedMotion`.
 */
export function PageLoading({
  label = 'Loading…',
  delayMs = LOADING_DELAY_MS,
  minHeight = '50vh',
}: PageLoadingProps) {
  const visible = useDelayedFlag(delayMs);
  if (!visible) return null;
  return (
    <Center
      flex={1}
      w="100%"
      mih={minHeight}
      p="xl"
      role="status"
      aria-live="polite"
      aria-busy
      data-testid="page-loading"
    >
      <Stack align="center" gap="md">
        <Loader size="lg" color="var(--charlie-accent-ink)" aria-hidden />
        <Text size="sm" c="dimmed">
          {label}
        </Text>
      </Stack>
    </Center>
  );
}
